/**
 * Persist auth tokens in localStorage so installed PWA / browser restarts stay signed in until logout.
 * (sessionStorage cleared when the standalone app or tab session ends.)
 */

const ACCESS_KEY = 'qnaread.accessToken';
const REFRESH_KEY = 'qnaread.refreshToken';

let accessToken = null;
let refreshToken = null;
let migratedFromSession = false;

function migrateFromSessionStorage() {
  if (migratedFromSession) return;
  migratedFromSession = true;
  try {
    const sessionAccess = sessionStorage.getItem(ACCESS_KEY);
    const sessionRefresh = sessionStorage.getItem(REFRESH_KEY);
    if (sessionAccess && !localStorage.getItem(ACCESS_KEY)) {
      localStorage.setItem(ACCESS_KEY, sessionAccess);
    }
    if (sessionRefresh && !localStorage.getItem(REFRESH_KEY)) {
      localStorage.setItem(REFRESH_KEY, sessionRefresh);
    }
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
}

function readStored() {
  migrateFromSessionStorage();
  try {
    if (!accessToken) {
      accessToken = localStorage.getItem(ACCESS_KEY) || null;
    }
    if (!refreshToken) {
      refreshToken = localStorage.getItem(REFRESH_KEY) || null;
    }
  } catch {
    /* private mode / blocked storage */
  }
}

export function getAccessToken() {
  readStored();
  return accessToken;
}

export function getRefreshToken() {
  readStored();
  return refreshToken;
}

export function hasPersistedSession() {
  return Boolean(getRefreshToken() || getAccessToken());
}

export function setAuthTokens(nextAccess, nextRefresh) {
  accessToken = nextAccess || null;
  refreshToken = nextRefresh || null;
  try {
    if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
    else localStorage.removeItem(ACCESS_KEY);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    else localStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(ACCESS_KEY);
    sessionStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
}

/** Persist tokens returned by /auth/login, /auth/google, /auth/refresh, etc. */
export function applyAuthTokensFromResponse(data) {
  if (!data || typeof data !== 'object') return;
  const access = data.accessToken || data.access_token;
  const refresh = data.refreshToken || data.refresh_token;
  if (access || refresh) {
    setAuthTokens(access || getAccessToken(), refresh || getRefreshToken());
  }
}

export function clearAuthTokens() {
  accessToken = null;
  refreshToken = null;
  setAuthTokens(null, null);
}

export function authHeaderValue() {
  const token = getAccessToken();
  return token ? `Bearer ${token}` : null;
}
